import React, { useEffect, useMemo, useState } from "react";
import { freeOver } from "@/lib/storenav";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ChevronDown, Droplets, Feather, Heart, Leaf, Recycle, ShieldCheck, Sparkles, Star, Truck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { Reveal } from "@/components/pharmacy/hooks";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { V, VTitle, VButton, VerdantCard } from "@/components/verdant/VerdantParts";
import { homeSets, discountPct } from "@/lib/homesets";

const wrap = "max-w-6xl mx-auto px-5";

const FEATURES = [
  [Leaf, "Plant-based goodness", "Aloe vera, neem, tulsi, rose and lemon — ingredients you can recognise."],
  [Droplets, "Gentle on skin", "Glycerine and goat-milk blends that cleanse without stripping moisture."],
  [Feather, "Handmade in small batches", "Cut, cured and packed by hand so every bar is fresh."],
  [ShieldCheck, "Skin-friendly formulas", "Made to be kind to everyday skin, for the whole family."],
  [Recycle, "Mindful packaging", "Simple, minimal wrapping that wastes less."],
  [Truck, "Delivered across India", "Fast, safe shipping and easy order tracking."],
];

const FAQS = [
  ["What are your soaps made of?", "Each product page lists the key ingredients. Our bars are handmade with natural oils, glycerine and botanicals such as aloe vera, neem, tulsi, rose and lemon."],
  ["Are they suitable for sensitive skin?", "They are made to be gentle for everyday use. If your skin is very sensitive, we suggest a small patch test before first use."],
  ["How long does delivery take?", "Orders are packed quickly and shipped across India. You can follow your parcel any time from the Track Order page."],
  ["Can I return an item?", "Yes — please read our Return Policy in the footer. Unopened products can be returned as described there."],
];

const SAMPLE_REVIEWS = [
  ["Aanya S.", "My skin feels soft and fresh after every bath. The lemon soap smells wonderful and lasts long."],
  ["Rohit M.", "Genuinely gentle. I switched the whole family to these bars and nobody has had any dryness."],
  ["Priya K.", "Lovely packaging, quick delivery and the rose goat-milk soap is now my favourite."],
];

export default function VerdantHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  const all = s.products;
  const sets = useMemo(() => homeSets(all, { fresh: 8, featured: 4, sale: 4 }), [all]);
  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-green-500 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const { slug, store } = s;
  const lead = (all.filter((p) => p.featured && p.image).length ? all.filter((p) => p.featured && p.image) : all.filter((p) => p.image));
  const pic = (i) => lead[i % Math.max(lead.length, 1)]?.image;
  const cats = s.categories.map((name) => ({ name, count: all.filter((p) => p.category === name).length, image: all.find((p) => p.category === name && p.image)?.image }));
  const maxOff = all.reduce((m, p) => Math.max(m, discountPct(p)), 0);
  const shown = all.filter((p) => `${p.name} ${p.category || ""}`.toLowerCase().includes(q));

  if (q) {
    return (
      <StoreShell>
        <section id="shop" className={`${wrap} py-12`} data-testid="verdant-search-results">
          <VTitle eyebrow="Search">Results for “{q}”</VTitle>
          {shown.length === 0 ? <div className="text-center py-16 text-slate-500">No products found.</div> : (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="storefront-products">{shown.map((p) => <VerdantCard key={p.id} p={p} s={s} cart={cart} />)}</div>
          )}
        </section>
      </StoreShell>
    );
  }

  return (
    <StoreShell>
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `radial-gradient(1200px 500px at 80% 0%, ${V.glow} 0%, ${V.deep} 60%)`, color: "#fff" }} data-testid="verdant-hero">
        <div className={`${wrap} py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center`}>
          <Reveal kind="rv-left">
            <div className="inline-flex items-center gap-2 text-[12px] font-semibold" style={{ color: V.leaf }}><span className="w-2 h-2 rounded-full" style={{ background: V.lemon }} />Discover the power of nature</div>
            <h1 className="mt-4 text-[38px] md:text-[54px] leading-[1.08] font-medium" style={{ fontFamily: V.serif }}>
              {store.tagline ? store.tagline : <>Pure care, <em className="not-italic" style={{ color: V.lemon }}>naturally</em> made for your skin.</>}
            </h1>
            <p className="mt-5 text-[15px] max-w-md" style={{ color: "#c9dcc0" }}>Handmade soaps and face care from {store.name}, crafted with plant-based ingredients and delivered to your door.</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <VButton to={`/store/${slug}/shop`} variant="lemon" testId="verdant-hero-cta">Shop now</VButton>
              <a href="#categories" className="text-[13px] font-semibold underline underline-offset-4" style={{ color: V.leaf }}>Browse categories</a>
            </div>
          </Reveal>
          <Reveal kind="rv-right" className="relative h-[300px] sm:h-[380px]">
            {[0, 1, 2].map((i) => pic(i) && (
              <div key={i} className="absolute rounded-3xl overflow-hidden shadow-2xl rx-float" style={{
                width: i === 1 ? 210 : 170, height: i === 1 ? 270 : 220, background: V.mist, border: `4px solid ${i === 1 ? V.lemon : "rgba(255,255,255,.9)"}`,
                left: i === 0 ? "2%" : i === 1 ? "30%" : "62%", top: i === 1 ? 0 : 70, zIndex: i === 1 ? 2 : 1, animationDelay: `${i * 0.6}s`, transform: `rotate(${(i - 1) * 5}deg)` }}>
                <img src={pic(i)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      {cats.length > 0 && (
        <section id="categories" className={`${wrap} py-12`}>
          <div className="grid sm:grid-cols-3 gap-4">
            {cats.slice(0, 6).map((c, k) => (
              <Reveal key={c.name} delay={k * 80} kind="rv-zoom">
                <Link to={`/store/${slug}/shop?category=${encodeURIComponent(c.name)}`} data-testid={`verdant-cat-${c.name}`} className="group flex items-center gap-4 rounded-3xl p-3 transition-shadow hover:shadow-lg" style={{ background: V.mist, border: `1px solid ${V.line}` }}>
                  <span className="w-20 h-20 rounded-2xl overflow-hidden shrink-0" style={{ background: "#fff" }}>{c.image && <img src={c.image} alt="" loading="lazy" className="w-full h-full object-cover transition-transform group-hover:scale-110" />}</span>
                  <span><span className="block text-[17px] font-medium" style={{ fontFamily: V.serif, color: V.deep }}>{c.name}</span><span className="text-[12px]" style={{ color: V.muted }}>{c.count} product{c.count === 1 ? "" : "s"}</span></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* About */}
      <section className="py-12" style={{ background: V.cream }}>
        <div className={`${wrap} grid md:grid-cols-2 gap-10 items-center`}>
          <Reveal kind="rv-left" className="relative">
            <div className="rounded-[2rem] overflow-hidden aspect-[4/4.2]" style={{ background: V.mist }}>{pic(3) && <img src={pic(3)} alt="" loading="lazy" className="w-full h-full object-cover" />}</div>
            <div className="absolute -bottom-5 right-4 rounded-2xl px-5 py-4 shadow-xl" style={{ background: V.lemon, color: V.deep }}>
              <div className="text-[26px] font-semibold leading-none" style={{ fontFamily: V.serif }}>100%</div><div className="text-[12px] font-semibold">Natural ingredients</div>
            </div>
          </Reveal>
          <Reveal kind="rv-right">
            <VTitle eyebrow="About us">Pure essence, <em style={{ color: V.em }}>gentle care</em> every day</VTitle>
            <p className="mt-4 text-[14px] leading-relaxed" style={{ color: V.muted }}>{store.name} makes small-batch soaps and face care from botanicals, natural oils and glycerine — simple formulas that clean well and feel kind to your skin.</p>
            <ul className="mt-5 space-y-3">{["Handmade in small batches", "Skin-friendly, plant-based ingredients", "Delivered across India with easy tracking"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-[14px]" style={{ color: V.deep }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: V.primary }}><Check size={13} color={V.deep} /></span>{t}</li>))}</ul>
            <div className="mt-7"><VButton to={`/store/${slug}/about`}>Learn more</VButton></div>
          </Reveal>
        </div>
      </section>

      {/* New arrivals */}
      {sets.fresh.length > 0 && (
        <section id="new-arrivals" className={`${wrap} py-14`}>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <VTitle eyebrow="Our products">Soaps &amp; face care for <em style={{ color: V.em }}>pure, gentle skin</em></VTitle>
            <VButton to={`/store/${slug}/shop`}>View all products</VButton>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="storefront-products">
            {sets.fresh.map((p, k) => <Reveal key={p.id} delay={(k % 4) * 70} kind="rv-zoom"><VerdantCard p={p} s={s} cart={cart} /></Reveal>)}
          </div>
        </section>
      )}

      {/* Why us (dark) */}
      <section style={{ background: V.deep, color: "#fff" }}>
        <div className={`${wrap} py-16 grid md:grid-cols-2 gap-10 items-center`}>
          <Reveal kind="rv-left" className="grid grid-cols-2 gap-4">
            {[4, 5].map((i, k) => pic(i) && <div key={i} className={`rounded-3xl overflow-hidden aspect-[3/4] ${k ? "mt-8" : ""}`} style={{ background: V.forest }}><img src={pic(i)} alt="" loading="lazy" className="w-full h-full object-cover" /></div>)}
          </Reveal>
          <Reveal kind="rv-right">
            <VTitle eyebrow="What we do" light>Delivering <em style={{ color: V.lemon }}>purity</em> and care, daily</VTitle>
            <p className="mt-4 text-[14px] leading-relaxed" style={{ color: "#c9dcc0" }}>From the first batch to your doorstep, every step is about keeping things simple and natural — thoughtfully sourced botanicals, careful curing and honest packaging.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[[Sparkles, "Crafted for fresh skin", "Small batches, carefully cured."], [Heart, "Made with care", "Every bar is checked before it ships."]].map(([I, h, d]) => (
                <div key={h} className="rounded-2xl p-4" style={{ background: V.forest, border: "1px solid rgba(255,255,255,.08)" }}>
                  <I size={20} style={{ color: V.lemon }} /><div className="mt-2 text-[15px] font-medium" style={{ fontFamily: V.serif }}>{h}</div><p className="text-[12px] mt-1" style={{ color: "#a9c4a0" }}>{d}</p>
                </div>))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className={`${wrap} py-16`}>
        <VTitle eyebrow="Our benefits" center>Why customers choose <em style={{ color: V.em }}>{store.name}</em></VTitle>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map(([I, h, d], k) => (
            <Reveal key={h} delay={(k % 3) * 80} kind="rv-zoom">
              <div className="h-full rounded-3xl p-6" style={{ background: V.cream, border: `1px solid ${V.line}` }}>
                <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: V.primary, color: V.deep }}><I size={20} /></span>
                <h3 className="mt-4 text-[18px] font-medium" style={{ fontFamily: V.serif, color: V.deep }}>{h}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: V.muted }}>{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Best sellers / sale */}
      {(sets.featured.length > 0 || sets.sale.length > 0) && (
        <section id="bestsellers" style={{ background: V.cream }}>
          <div className={`${wrap} py-14`}>
            <VTitle eyebrow="Customer favourites" center>Our <em style={{ color: V.em }}>most loved</em> picks</VTitle>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5">
              {(sets.featured.length ? sets.featured : sets.sale).map((p, k) => <Reveal key={p.id} delay={k * 80} kind="rv-zoom"><VerdantCard p={p} s={s} cart={cart} /></Reveal>)}
            </div>
          </div>
        </section>
      )}

      {/* Promo */}
      <section id="deals" style={{ background: `linear-gradient(120deg, ${V.deep}, ${V.glow})`, color: "#fff" }}>
        <div className={`${wrap} py-14 grid md:grid-cols-2 gap-8 items-center`}>
          <Reveal kind="rv-left">
            <div className="text-[13px] font-semibold" style={{ color: V.leaf }}>Limited time offer</div>
            <h2 className="mt-2 text-[34px] md:text-[48px] leading-tight font-medium" style={{ fontFamily: V.serif }}>{maxOff >= 5 ? <>Up to <span style={{ color: V.lemon }}>{maxOff}% off</span> on soaps</> : (freeOver(store) > 0 ? <>Free delivery over <span style={{ color: V.lemon }}>₹{freeOver(store).toLocaleString("en-IN")}</span></> : <>Soaps &amp; face care, <span style={{ color: V.lemon }}>delivered</span> to your door</>)}</h2>
            <p className="mt-3 text-[14px] max-w-md" style={{ color: "#c9dcc0" }}>Stock up on your favourites — combo packs and multi-bar bundles give you the best value.</p>
            <div className="mt-6"><VButton to={`/store/${slug}/shop?sort=price-asc`} variant="lemon">Shop the offers</VButton></div>
          </Reveal>
          <Reveal kind="rv-right" className="flex gap-4 justify-center">
            {(sets.sale.length ? sets.sale : lead).slice(0, 3).map((p, i) => <Link key={p.id} to={`/store/${slug}/product/${p.id}`} className={`w-28 sm:w-36 aspect-[3/4] rounded-3xl overflow-hidden ${i === 1 ? "mt-0" : "mt-8"}`} style={{ background: V.forest, border: `3px solid ${i === 1 ? V.lemon : "rgba(255,255,255,.2)"}` }}>{p.image && <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover" />}</Link>)}
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <Faq />

      {/* Managed sections */}
      <ManagedReviews s={s} layout="verdant" fallback={<SampleReviews />} />
      <ManagedInstagram s={s} layout="verdant" />
    </StoreShell>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className={`${wrap} py-16 grid md:grid-cols-[1fr_1.3fr] gap-10`} data-testid="verdant-faq">
      <VTitle eyebrow="FAQs">Your questions, <em style={{ color: V.em }}>our answers</em></VTitle>
      <div className="space-y-3">
        {FAQS.map(([q, a], i) => (
          <div key={q} className="rounded-2xl overflow-hidden" style={{ background: open === i ? V.primary : V.cream, border: `1px solid ${V.line}` }}>
            <button type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 text-[14px] font-semibold" style={{ color: V.deep }}>
              {q}<ChevronDown size={18} className="shrink-0 transition-transform" style={{ transform: open === i ? "rotate(180deg)" : "none" }} />
            </button>
            {open === i && <p className="px-5 pb-4 text-[13px] leading-relaxed" style={{ color: V.deep }}>{a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

function SampleReviews() {
  return (
    <section id="reviews" style={{ background: V.cream }}>
      <div className={`${wrap} py-16`}>
        <VTitle eyebrow="Testimonials" center>Real reviews, <em style={{ color: V.em }}>happy customers</em></VTitle>
        <div className="mt-9 grid md:grid-cols-3 gap-5">
          {SAMPLE_REVIEWS.map(([name, text], k) => (
            <Reveal key={name} delay={k * 90} kind="rv-zoom">
              <div className="h-full rounded-3xl p-6 bg-white" style={{ border: `1px solid ${V.line}` }}>
                <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} fill="#f5b50a" color="#f5b50a" />)}</div>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: V.muted }}>{text}</p>
                <div className="mt-4 text-[14px] font-semibold" style={{ color: V.deep, fontFamily: V.serif }}>{name}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
